import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, ExternalLink, FileText, RefreshCw, Search } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Loader, Pill } from "@/components/Bits";
import { Markdown } from "@/components/Markdown";

const typeTone = { paper: "blue", book: "amber", web: "slate", video: "green" };

export const ReadingPanel = ({ topicId }) => {
  const [doc, setDoc] = useState(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (refresh = false) => {
      try {
        const { data } = await api.get(`/topics/${topicId}/material${refresh ? "?refresh=true" : ""}`);
        setDoc(data);
        return data;
      } catch (err) {
        toast.error(errMsg(err));
      }
    },
    [topicId],
  );

  useEffect(() => {
    setDoc(null);
    load();
  }, [load]);

  const generating = doc?.status === "generating";

  useEffect(() => {
    if (!generating) return;
    const id = setInterval(() => load(), 8000);
    return () => clearInterval(id);
  }, [generating, load]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/topics/${topicId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.title}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Printable booklet downloaded");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!doc || generating)
    return (
      <Loader
        label="Teacher & Research agents are preparing this topic…"
        note="Real sources are retrieved from arXiv, Open Library and encyclopaedias, then in-depth material is written around them."
      />
    );

  const sources = (doc.sources || []).filter((s) => !query || `${s.title} ${s.authors} ${s.type}`.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="grid lg:grid-cols-12 gap-10" data-testid="reading-panel">
      <article className="lg:col-span-8">
        <Markdown text={doc.material} />
      </article>
      <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start space-y-4">
        <div className="card-flat p-6">
          <div className="flex items-center justify-between gap-3">
            <p className="rule-label">References ({(doc.sources || []).length})</p>
            <button data-testid="reader-refresh-sources" onClick={() => load(true)} title="Refresh sources & regenerate" className="p-1.5 rounded-full hover:bg-slate-100">
              <RefreshCw className="w-3.5 h-3.5 text-slate-500" />
            </button>
          </div>
          <div className="mt-3 relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-3 text-slate-400" />
            <input data-testid="reader-source-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter sources" className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-blue-500" />
          </div>
          <ol className="mt-4 space-y-4" data-testid="reader-citations">
            {sources.map((s, i) => (
              <li key={s.url} id={`src-${i + 1}`} className="text-sm">
                <div className="flex items-start gap-2">
                  <span className="font-mono-x text-xs text-slate-400 mt-0.5">[{i + 1}]</span>
                  <div>
                    <a href={s.url} target="_blank" rel="noreferrer" className="font-semibold text-slate-800 hover:text-blue-700 inline-flex items-start gap-1">
                      {s.title} <ExternalLink className="w-3 h-3 mt-1 shrink-0" />
                    </a>
                    <p className="text-xs text-slate-500 mt-0.5">{[s.authors, s.year].filter(Boolean).join(" · ")}</p>
                    {s.why && <p className="text-xs text-slate-500 mt-1 italic">{s.why}</p>}
                    <div className="mt-1">
                      <Pill tone={typeTone[s.type] || "slate"}>{s.type}</Pill>
                    </div>
                  </div>
                </div>
              </li>
            ))}
            {!sources.length && <p className="text-sm text-slate-500">No sources match that filter.</p>}
          </ol>
        </div>
        <button data-testid="reader-download-pdf" onClick={download} disabled={busy} className="w-full card-flat p-5 flex items-center gap-3 hover:!border-slate-900 disabled:opacity-50">
          <FileText className="w-5 h-5 text-teal-700" />
          <span className="text-left">
            <span className="block font-display font-semibold">{busy ? "Building PDF…" : "Download printable PDF"}</span>
            <span className="block text-xs text-slate-500">References appended at the end</span>
          </span>
          <Download className="w-4 h-4 ml-auto text-slate-400" />
        </button>
      </aside>
    </div>
  );
};
